// /admin/empresas — listado de empresas B2B con filtro de pipeline y búsqueda.
// Click en fila navega a /admin/empresas/:id (detalle con tabs).
// El alta de empresa nueva se hace desde acá (botón "+ Nueva empresa") o por
// conversión de un lead desde /admin/consultas-comerciales.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const PIPELINE = [
  { v: 'prospect',   l: 'Prospect',   color: '#0EA5E9', bg: '#E0F2FE', hint: 'Empresa cargada, todavía no contactada' },
  { v: 'cotizando',  l: 'Cotizando',  color: '#CA8A04', bg: '#FEF9C3', hint: 'En charla / armando presupuesto' },
  { v: 'negociando', l: 'Negociando', color: '#7C3AED', bg: '#EDE9FE', hint: 'Presupuesto enviado, esperando respuesta' },
  { v: 'activo',     l: 'Activo',     color: '#16A34A', bg: '#DCFCE7', hint: 'Cliente B2B con suscripción / pedidos activos' },
  { v: 'pausado',    l: 'Pausado',    color: '#64748B', bg: '#F1F5F9', hint: 'Cliente histórico que pausó' },
  { v: 'perdido',    l: 'Perdido',    color: '#DC2626', bg: '#FEE2E2', hint: 'Rechazó o no respondió más' },
]

const TIPO_LABEL = {
  comercio: 'Comercio',
  oficina: 'Oficina',
  administradora: 'Administradora de edificios',
  otro: 'Otro',
}

export default function Empresas() {
  const nav = useNavigate()
  const [empresas, setEmpresas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [mostrarAlta, setMostrarAlta] = useState(false)
  const [mostrarArchivadas, setMostrarArchivadas] = useState(false)
  const [seleccionadas, setSeleccionadas] = useState(new Set())
  const [accionBulk, setAccionBulk] = useState(null) // {tipo, ids}
  const [owners, setOwners] = useState([])

  useEffect(() => {
    // Traer usuarios admin/superadmin/comercial para el picker de owner
    supabase.from('profiles').select('id, nombre, role').in('role', ['admin', 'superadmin', 'comercial']).then(({ data }) => setOwners(data || []))
  }, [])

  function toggleSel(id, ev) {
    ev?.stopPropagation()
    const s = new Set(seleccionadas)
    s.has(id) ? s.delete(id) : s.add(id)
    setSeleccionadas(s)
  }
  function toggleTodas(idsVisibles) {
    if (seleccionadas.size === idsVisibles.length) setSeleccionadas(new Set())
    else setSeleccionadas(new Set(idsVisibles))
  }

  async function cargar() {
    setCargando(true)
    setError(null)
    // Traemos empresas + agregados: cantidad de presupuestos, última interacción.
    let q = supabase
      .from('empresas')
      .select(`
        id, razon_social, rut, tipo, estado_pipeline, facturacion_modo,
        email_facturacion, telefono, contacto_principal_nombre,
        owner_id, consulta_origen_id, notas, created_at, updated_at, archivada_at,
        owner:profiles!empresas_owner_id_fkey(id, nombre),
        presupuestos(count),
        empresas_interacciones(fecha, tipo, resumen)
      `)
      .order('updated_at', { ascending: false })
    if (!mostrarArchivadas) q = q.is('archivada_at', null)
    const { data, error: e } = await q
    if (e) {
      setError(e.message)
      setEmpresas([])
    } else {
      // Normalizar: última interacción
      const lista = (data || []).map(e => {
        const interacciones = e.empresas_interacciones || []
        const ultima = interacciones.length
          ? [...interacciones].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))[0]
          : null
        return { ...e, ultima_interaccion: ultima, total_presupuestos: e.presupuestos?.[0]?.count || 0 }
      })
      setEmpresas(lista)
    }
    setCargando(false)
  }

  useEffect(() => { cargar() }, [mostrarArchivadas])

  const filtradas = useMemo(() => {
    let f = empresas
    if (filtroEstado !== 'todos') f = f.filter(e => e.estado_pipeline === filtroEstado)
    if (filtroTipo !== 'todos') f = f.filter(e => e.tipo === filtroTipo)
    if (busqueda) {
      const q = busqueda.toLowerCase()
      f = f.filter(e =>
        e.razon_social?.toLowerCase().includes(q) ||
        e.rut?.includes(q) ||
        e.contacto_principal_nombre?.toLowerCase().includes(q) ||
        e.telefono?.includes(q) ||
        e.email_facturacion?.toLowerCase().includes(q)
      )
    }
    return f
  }, [empresas, filtroEstado, filtroTipo, busqueda])

  const conteoEstado = useMemo(() => {
    const c = { todos: empresas.length }
    PIPELINE.forEach(e => { c[e.v] = empresas.filter(x => x.estado_pipeline === e.v).length })
    return c
  }, [empresas])

  return (
    <AdminLayout title="Empresas (B2B)">
      <Estilos />
      <div className="emp-page">
        <header className="emp-head">
          <div>
            <h1>Empresas (B2B)</h1>
            <p>Cuentas comerciales — comercios, oficinas y administradoras. Pipeline, presupuestos y seguimiento en un solo lugar.</p>
          </div>
          <div className="emp-head-actions">
            <button onClick={cargar} className="emp-btn-secondary">↻ Actualizar</button>
            <button onClick={() => setMostrarAlta(true)} className="emp-btn-primary">+ Nueva empresa</button>
          </div>
        </header>

        <div className="emp-filters">
          <FiltroChip label={`Todos · ${conteoEstado.todos}`} active={filtroEstado === 'todos'} onClick={() => setFiltroEstado('todos')} />
          {PIPELINE.map(e => (
            <FiltroChip
              key={e.v}
              label={`${e.l} · ${conteoEstado[e.v]}`}
              active={filtroEstado === e.v}
              onClick={() => setFiltroEstado(e.v)}
              color={e.color}
              bg={e.bg}
              title={e.hint}
            />
          ))}
        </div>

        <div className="emp-row">
          <select className="emp-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="todos">Todos los tipos</option>
            {Object.entries(TIPO_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Buscar por razón social, RUT, contacto, teléfono..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="emp-search"
          />
          <label className="emp-archivadas-toggle" title="Incluir empresas archivadas">
            <input type="checkbox" checked={mostrarArchivadas} onChange={e => setMostrarArchivadas(e.target.checked)} />
            <span>📦 Mostrar archivadas</span>
          </label>
        </div>

        {error && <div className="emp-error">⚠ {error}</div>}

        {cargando ? (
          <div className="emp-loading">Cargando empresas...</div>
        ) : filtradas.length === 0 ? (
          <div className="emp-empty">
            <div style={{ fontSize: 48 }}>🏢</div>
            <h3>{empresas.length === 0 ? 'Todavía no hay empresas' : 'Sin resultados'}</h3>
            <p>
              {empresas.length === 0
                ? 'Cargá una manualmente con "+ Nueva empresa" o convertí un lead desde Consultas comerciales.'
                : 'Probá con otro filtro o búsqueda.'}
            </p>
          </div>
        ) : (
          <table className="emp-table">
            <thead>
              <tr>
                <th>Razón social</th>
                <th>Tipo</th>
                <th>Contacto</th>
                <th>Owner</th>
                <th>Pipeline</th>
                <th>Presup.</th>
                <th>Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(e => {
                const estado = PIPELINE.find(p => p.v === e.estado_pipeline) || PIPELINE[0]
                return (
                  <tr key={e.id} className={`emp-row-tr${e.archivada_at ? ' emp-row-archivada' : ''}`} onClick={() => nav(`/admin/empresas/${e.id}`)}>
                    <td>
                      <strong>{e.razon_social}</strong>
                      {e.archivada_at && <span className="emp-pill-arch">📦 archivada</span>}
                      {e.rut && <div className="emp-rut">RUT {e.rut}</div>}
                    </td>
                    <td>{TIPO_LABEL[e.tipo] || e.tipo}</td>
                    <td>
                      <div>{e.contacto_principal_nombre || '—'}</div>
                      <div className="emp-meta">{e.telefono || ''} {e.email_facturacion ? `· ${e.email_facturacion}` : ''}</div>
                    </td>
                    <td>{e.owner?.nombre || '—'}</td>
                    <td>
                      <span className="emp-badge" style={{ background: estado.bg, color: estado.color }}>{estado.l}</span>
                    </td>
                    <td className="emp-count">{e.total_presupuestos}</td>
                    <td className="emp-fecha">
                      {e.ultima_interaccion
                        ? `${formatFecha(e.ultima_interaccion.fecha)} · ${e.ultima_interaccion.tipo}`
                        : formatFecha(e.updated_at) + ' (sin interacciones)'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {mostrarAlta && (
          <ModalAlta
            onClose={() => setMostrarAlta(false)}
            onCreated={(id) => {
              setMostrarAlta(false)
              nav(`/admin/empresas/${id}`)
            }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

function FiltroChip({ label, active, onClick, color = '#1A3461', bg = '#E2E8F0', title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`emp-chip${active ? ' active' : ''}`}
      style={active ? { background: color, color: '#fff', borderColor: color } : { background: bg, color: '#1A3461' }}
    >
      {label}
    </button>
  )
}

function ModalAlta({ onClose, onCreated }) {
  const [razonSocial, setRazonSocial] = useState('')
  const [rut, setRut] = useState('')
  const [tipo, setTipo] = useState('comercio')
  const [contacto, setContacto] = useState('')
  const [telefono, setTelefono] = useState('')
  const [email, setEmail] = useState('')
  const [notas, setNotas] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  async function guardar() {
    setError(null)
    if (!razonSocial.trim()) {
      setError('La razón social es obligatoria')
      return
    }
    setGuardando(true)
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error: e } = await supabase
      .from('empresas')
      .insert({
        razon_social: razonSocial.trim(),
        rut: rut.trim() || null,
        tipo,
        contacto_principal_nombre: contacto.trim() || null,
        telefono: telefono.trim() || null,
        email_facturacion: email.trim() || null,
        notas: notas.trim() || null,
        owner_id: user?.id || null,
        created_by: user?.id || null,
      })
      .select('id')
      .single()
    setGuardando(false)
    if (e) {
      setError(e.message)
      return
    }
    onCreated(data.id)
  }

  return (
    <div className="emp-modal-bg" onClick={onClose}>
      <div className="emp-modal" onClick={ev => ev.stopPropagation()}>
        <header className="emp-modal-head">
          <h3>Nueva empresa</h3>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </header>

        <div className="emp-modal-body">
          <label className="emp-label">
            <span>Razón social <em>*</em></span>
            <input value={razonSocial} onChange={e => setRazonSocial(e.target.value)} placeholder="Ej: Edificio Sarandí 612 SCC" autoFocus />
          </label>

          <div className="emp-grid-2">
            <label className="emp-label">
              <span>RUT</span>
              <input value={rut} onChange={e => setRut(e.target.value)} placeholder="Opcional al inicio" />
            </label>
            <label className="emp-label">
              <span>Tipo</span>
              <select value={tipo} onChange={e => setTipo(e.target.value)}>
                {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>

          <label className="emp-label">
            <span>Contacto principal</span>
            <input value={contacto} onChange={e => setContacto(e.target.value)} placeholder="Nombre + cargo" />
          </label>

          <div className="emp-grid-2">
            <label className="emp-label">
              <span>Teléfono</span>
              <input value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="+598..." />
            </label>
            <label className="emp-label">
              <span>Email de facturación</span>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@empresa.com" />
            </label>
          </div>

          <label className="emp-label">
            <span>Notas internas</span>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={3} placeholder="Lo que sepas: cómo llegaron, qué necesitan, particularidades..." />
          </label>

          {error && <div className="emp-error">⚠ {error}</div>}
        </div>

        <footer className="emp-modal-foot">
          <button className="emp-btn-secondary" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button className="emp-btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Crear y abrir'}
          </button>
        </footer>
      </div>
    </div>
  )
}

function formatFecha(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function Estilos() {
  return (
    <style>{`
      .emp-page { max-width: 1280px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }

      .emp-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
      .emp-head h1 { font-size: 24px; font-weight: 900; margin: 0 0 4px; letter-spacing: -0.02em; }
      .emp-head p { font-size: 13.5px; color: #64748B; margin: 0; max-width: 720px; line-height: 1.5; }
      .emp-head-actions { display: flex; gap: 8px; flex-shrink: 0; }

      .emp-btn-primary, .emp-btn-secondary {
        padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 700;
        font-family: inherit; cursor: pointer; transition: all 0.15s; border: none;
        display: inline-flex; align-items: center; gap: 6px;
      }
      .emp-btn-primary { background: #1A3461; color: #fff; }
      .emp-btn-primary:hover { background: #1A3461; }
      .emp-btn-primary:disabled { opacity: 0.5; cursor: wait; }
      .emp-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .emp-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }
      .emp-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

      .emp-filters { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
      .emp-chip {
        padding: 6px 12px; border-radius: 999px; border: 1.5px solid transparent;
        font-size: 12.5px; font-weight: 700; cursor: pointer;
        font-family: inherit; transition: all 0.12s;
      }
      .emp-chip:hover { filter: brightness(0.96); }

      .emp-row { display: flex; gap: 10px; margin-bottom: 14px; }
      .emp-select, .emp-search {
        padding: 9px 12px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461;
        background: #fff;
      }
      .emp-select { width: 220px; }
      .emp-search { flex: 1; }
      .emp-select:focus, .emp-search:focus { outline: none; border-color: #00A8B5; }

      .emp-error {
        background: #FEE2E2; border-left: 3px solid #DC2626;
        padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B;
        font-size: 13px; margin-bottom: 12px;
      }
      .emp-loading {
        text-align: center; padding: 40px; color: #64748B; font-size: 14px;
      }
      .emp-empty {
        text-align: center; padding: 60px 20px; color: #64748B;
        background: #fff; border: 1px dashed #E2E8F0; border-radius: 12px;
      }
      .emp-empty h3 { margin: 12px 0 4px; color: #1A3461; font-size: 17px; }
      .emp-empty p { margin: 0; font-size: 13.5px; line-height: 1.5; }

      .emp-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(15,23,42,0.05); }
      .emp-table th {
        background: #F8FAFC; text-align: left; padding: 11px 14px;
        font-size: 11.5px; font-weight: 700; color: #64748B;
        text-transform: uppercase; letter-spacing: 0.05em;
        border-bottom: 1px solid #E2E8F0;
      }
      .emp-table td { padding: 12px 14px; border-bottom: 1px solid #F1F5F9; font-size: 13.5px; vertical-align: top; }
      .emp-table tr:last-child td { border-bottom: none; }
      .emp-row-tr { cursor: pointer; transition: background 0.1s; }
      .emp-row-tr:hover { background: #F8FAFC; }
      .emp-row-archivada { opacity: 0.55; }
      .emp-row-archivada:hover { opacity: 0.85; }
      .emp-pill-arch { background: #F1F5F9; color: #64748B; font-size: 10px; padding: 2px 8px; border-radius: 999px; font-weight: 700; margin-left: 8px; text-transform: uppercase; letter-spacing: 0.05em; vertical-align: middle; }

      .emp-archivadas-toggle {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 12px; background: #fff; border: 1.5px solid #E2E8F0;
        border-radius: 8px; font-size: 12.5px; color: #475569;
        cursor: pointer; user-select: none; white-space: nowrap;
      }
      .emp-archivadas-toggle:hover { border-color: #1A3461; }
      .emp-archivadas-toggle input { margin: 0; cursor: pointer; }

      .emp-rut { font-size: 11px; color: #64748B; margin-top: 2px; font-family: 'SF Mono', monospace; }
      .emp-meta { font-size: 11.5px; color: #64748B; margin-top: 2px; }
      .emp-fecha { font-size: 12px; color: #64748B; white-space: nowrap; }
      .emp-count { font-weight: 700; color: #1A3461; text-align: center; }
      .emp-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 700; }

      /* Modal alta */
      .emp-modal-bg {
        position: fixed; inset: 0; background: rgba(15,23,42,0.5);
        display: flex; align-items: center; justify-content: center;
        z-index: 100; padding: 16px;
      }
      .emp-modal {
        background: #fff; border-radius: 14px; max-width: 560px; width: 100%;
        max-height: 90vh; overflow: hidden; display: flex; flex-direction: column;
        box-shadow: 0 20px 40px rgba(15,23,42,0.2);
      }
      .emp-modal-head {
        display: flex; justify-content: space-between; align-items: center;
        padding: 16px 20px; border-bottom: 1px solid #E2E8F0;
      }
      .emp-modal-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #1A3461; }
      .emp-modal-close {
        background: transparent; border: none; font-size: 18px; cursor: pointer;
        color: #64748B; padding: 4px 8px;
      }
      .emp-modal-close:hover { color: #1A3461; }
      .emp-modal-body { padding: 18px 20px; overflow-y: auto; flex: 1; }
      .emp-modal-foot {
        display: flex; justify-content: flex-end; gap: 8px;
        padding: 14px 20px; border-top: 1px solid #E2E8F0; background: #F8FAFC;
      }

      .emp-label { display: block; margin-bottom: 14px; }
      .emp-label > span { display: block; font-size: 12px; font-weight: 700; color: #475569; margin-bottom: 5px; }
      .emp-label > span em { color: #DC2626; font-style: normal; }
      .emp-label input, .emp-label select, .emp-label textarea {
        width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff;
        box-sizing: border-box;
      }
      .emp-label textarea { resize: vertical; min-height: 60px; }
      .emp-label input:focus, .emp-label select:focus, .emp-label textarea:focus { outline: none; border-color: #00A8B5; }
      .emp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    `}</style>
  )
}
